import { useMemo } from 'react';

import { ANALYTICS_TIMEZONE } from '@apps/web/features/admin_agent_notes/model/constants';
import { resolveRangePreset } from '@apps/web/features/admin_agent_notes/model/range_presets';
import {
    type AgentNotesSearch,
    PER_PAGE,
} from '@apps/web/features/admin_agent_notes/model/search_params';
import {
    dateKeyToEndUtcIso,
    dateKeyToStartUtcIso,
    getDateStringInTimezone,
} from '@apps/web/lib/date_utils';

/**
 * Single source of truth for turning the agent-notes search params into the
 * list endpoint's query params. Fixed date keys win; otherwise a relative
 * `range` preset resolves against today. Date keys convert to instants in
 * the fixed analytics timezone so filter boundaries match the dashboard's
 * day semantics regardless of the admin's browser timezone.
 */
export function useAgentNotesQueryParams(search: AgentNotesSearch) {
    const {
        page,
        status,
        source,
        domain,
        organization_id: organizationId,
        created_after: createdAfter,
        created_before: createdBefore,
        range,
        search: searchQuery,
    } = search;

    const today = getDateStringInTimezone(new Date(), ANALYTICS_TIMEZONE);
    const hasFixedRange =
        createdAfter !== undefined || createdBefore !== undefined;
    const preset =
        !hasFixedRange && range !== undefined
            ? resolveRangePreset(range, today)
            : null;
    const fromKey = preset ? preset.from : createdAfter;
    const toKey = preset ? preset.to : createdBefore;

    return useMemo(
        () => ({
            page,
            perPage: PER_PAGE,
            organizationId,
            status,
            source,
            domain,
            createdAfter: fromKey
                ? dateKeyToStartUtcIso(fromKey, ANALYTICS_TIMEZONE)
                : undefined,
            createdBefore: toKey
                ? dateKeyToEndUtcIso(toKey, ANALYTICS_TIMEZONE)
                : undefined,
            search: searchQuery?.trim() || undefined,
        }),
        [
            page,
            organizationId,
            status,
            source,
            domain,
            fromKey,
            toKey,
            searchQuery,
        ],
    );
}
